/* eslint-disable */
// Session 43 · Reusable "Upload from device" widget for Admin edit forms.
// Session 44 · Added drag-to-reorder + delete + hero preview list so admins
// can control which photo becomes the directory-card hero.
//
// Reads a File as base64, POSTs to /api/admin/uploads/photo, and appends the
// returned public URL onto the caller's photos textarea value.
//
// Props:
//   value    — current textarea value (URLs, one per line)
//   onChange — receives the new value string
//   testid   — data-testid prefix ("ws-edit" | "venue-edit" | ...)
import { useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const MAX_BYTES = 6 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp";

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsDataURL(file);
  });
}

/**
 * Resolve a raw stored URL to something the <img> can render.
 * Backend returns paths like `/api/uploads/xxx.png` — the frontend needs
 * to hit REACT_APP_BACKEND_URL for those. Absolute URLs pass through.
 */
function resolveSrc(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/api/")) {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return base ? `${base}${u}` : u;
  }
  return u;
}

export default function AdminPhotoUploader({ value, onChange, testid = "admin", previewName, previewMeta, previewType, previewSlug, endpoint = "/admin/uploads/photo" }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const inputRef = useRef(null);

  // Convert textarea string → array (memoised so drag hover doesn't churn)
  const urls = useMemo(
    () => (value || "").split("\n").map(s => s.trim()).filter(Boolean),
    [value],
  );
  const heroSrc = urls[0] ? resolveSrc(urls[0]) : null;

  const commit = (list) => onChange?.(list.join("\n"));

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f && f.type && f.type.startsWith("image/"));
    if (!files.length) return;

    const oversized = files.filter(f => f.size > MAX_BYTES);
    if (oversized.length) {
      toast.error(`${oversized.length} file(s) exceed 6MB and were skipped`);
    }
    const safe = files.filter(f => f.size <= MAX_BYTES);
    if (!safe.length) return;

    setBusy(true);
    setProgress({ done: 0, total: safe.length });
    const newUrls = [];
    for (let i = 0; i < safe.length; i++) {
      const f = safe[i];
      try {
        const dataUrl = await readAsDataURL(f);
        const r = await api.post(endpoint, { data_url: dataUrl });
        if (r.data?.url) newUrls.push(r.data.url);
      } catch (e) {
        toast.error(`Upload failed for ${f.name}: ${e?.response?.data?.detail || e.message}`);
      }
      setProgress({ done: i + 1, total: safe.length });
    }

    if (newUrls.length) {
      commit([...urls, ...newUrls]);
      toast.success(`${newUrls.length} photo${newUrls.length === 1 ? "" : "s"} added`);
    }

    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const move = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= urls.length || to >= urls.length) return;
    const next = urls.slice();
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    commit(next);
  };

  const remove = (i) => {
    const next = urls.slice();
    next.splice(i, 1);
    commit(next);
  };

  const makeHero = (i) => move(i, 0);

  return (
    <div
      style={{ marginTop: 10 }}
      data-testid={`${testid}-photo-uploader`}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          style={{ display: "none" }}
          data-testid={`${testid}-photo-file-input`}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="m-btn-ghost"
          style={{ fontSize: 13, padding: "8px 14px" }}
          data-testid={`${testid}-photo-upload-btn`}
        >
          {busy
            ? `Uploading ${progress.done}/${progress.total}…`
            : "＋ Upload from device"}
        </button>
        <span style={{ fontSize: 12, color: "rgba(27,35,64,0.55)" }}>
          JPG / PNG / WebP · up to 6 MB · drag to reorder · first photo is the hero
        </span>
      </div>

      {urls.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr minmax(220px, 300px)",
            gap: 20,
            marginTop: 14,
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: 10,
            }}
            data-testid={`${testid}-photo-list`}
          >
          {urls.map((u, i) => {
            const isDragging = dragIdx === i;
            const isDragOver = dragOverIdx === i && dragIdx !== null && dragIdx !== i;
            return (
              <div
                key={`${u}-${i}`}
                draggable
                onDragStart={(e) => {
                  setDragIdx(i);
                  e.dataTransfer.effectAllowed = "move";
                  // Firefox needs data set to start drag
                  try { e.dataTransfer.setData("text/plain", String(i)); } catch { /* noop */ }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  if (dragOverIdx !== i) setDragOverIdx(i);
                }}
                onDragLeave={() => { if (dragOverIdx === i) setDragOverIdx(null); }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = dragIdx;
                  setDragIdx(null); setDragOverIdx(null);
                  if (from == null) return;
                  move(from, i);
                }}
                onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                style={{
                  position: "relative",
                  border: isDragOver
                    ? "2px dashed var(--gold, #B8913A)"
                    : "1px solid rgba(27,35,64,0.14)",
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#faf8f3",
                  cursor: "grab",
                  opacity: isDragging ? 0.4 : 1,
                  transition: "opacity 120ms, border 120ms",
                }}
                data-testid={`${testid}-photo-item-${i}`}
              >
                <div style={{
                  position: "relative", width: "100%",
                  paddingBottom: "62%", background: "rgba(27,35,64,0.05)",
                }}>
                  <img
                    src={resolveSrc(u)}
                    alt={`Photo ${i + 1}`}
                    loading="lazy"
                    style={{
                      position: "absolute", inset: 0, width: "100%", height: "100%",
                      objectFit: "cover", pointerEvents: "none",
                    }}
                  />
                  {i === 0 && (
                    <span
                      style={{
                        position: "absolute", top: 6, left: 6,
                        background: "var(--gold, #B8913A)", color: "#fff",
                        fontSize: 10, padding: "3px 8px", borderRadius: 999,
                        letterSpacing: "0.10em", textTransform: "uppercase",
                        fontWeight: 600,
                      }}
                      data-testid={`${testid}-photo-hero-badge`}
                    >
                      Hero
                    </span>
                  )}
                </div>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "6px 8px", fontSize: 11, color: "rgba(27,35,64,0.70)",
                  borderTop: "1px solid rgba(27,35,64,0.08)", background: "#fff",
                }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1 }}>⋮⋮</span>
                    #{i + 1}
                  </span>
                  <span style={{ display: "flex", gap: 6 }}>
                    {i !== 0 && (
                      <button
                        type="button"
                        onClick={() => makeHero(i)}
                        title="Make hero"
                        style={{
                          background: "transparent",
                          border: "1px solid rgba(27,35,64,0.20)",
                          borderRadius: 4, padding: "2px 6px",
                          fontSize: 10, cursor: "pointer",
                          color: "var(--m-navy, #1B2340)",
                        }}
                        data-testid={`${testid}-photo-make-hero-${i}`}
                      >
                        Make hero
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      title="Remove photo"
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(180,60,60,0.35)",
                        borderRadius: 4, padding: "2px 6px",
                        fontSize: 10, cursor: "pointer",
                        color: "rgb(180,60,60)",
                      }}
                      data-testid={`${testid}-photo-remove-${i}`}
                    >
                      Remove
                    </button>
                  </span>
                </div>
              </div>
            );
          })}
          </div>

          {/* Session 45 · Live directory-card preview column.
              Mirrors the SpacesDirectory card so admins see the marquee
              shot + meta line exactly as members will see it. */}
          <div
            style={{ position: "sticky", top: 20 }}
            data-testid={`${testid}-hero-preview`}
          >
            <div
              style={{
                fontSize: 10, letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(27,35,64,0.55)",
                fontWeight: 600, marginBottom: 8,
              }}
            >
              Directory Card Preview
            </div>
            <div
              style={{
                background: "#fff",
                border: "1px solid rgba(27,35,64,0.12)",
                borderRadius: 12,
                overflow: "hidden",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  paddingBottom: "62%",
                  background: "rgba(27,35,64,0.06)",
                }}
              >
                {heroSrc ? (
                  <img
                    src={heroSrc}
                    alt="Hero preview"
                    style={{
                      position: "absolute", inset: 0,
                      width: "100%", height: "100%",
                      objectFit: "cover",
                    }}
                    data-testid={`${testid}-hero-preview-img`}
                  />
                ) : (
                  <div
                    style={{
                      position: "absolute", inset: 0,
                      display: "flex", alignItems: "center",
                      justifyContent: "center",
                      color: "rgba(27,35,64,0.35)",
                      fontSize: 12,
                    }}
                    data-testid={`${testid}-hero-preview-empty`}
                  >
                    Add a photo to preview the hero
                  </div>
                )}
                {previewType && (
                  <span
                    style={{
                      position: "absolute", top: 10, left: 10,
                      background: "rgba(255,255,255,0.92)",
                      color: "var(--m-navy, #1B2340)",
                      fontSize: 10, padding: "4px 10px",
                      borderRadius: 999,
                      letterSpacing: "0.10em",
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    {previewType}
                  </span>
                )}
              </div>
              <div style={{ padding: "14px 16px 16px" }}>
                <div
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: 18, lineHeight: 1.25,
                    color: "var(--m-navy, #1B2340)",
                    marginBottom: 4,
                  }}
                >
                  {previewName || "Untitled listing"}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(27,35,64,0.60)",
                    lineHeight: 1.5,
                  }}
                >
                  {previewMeta || "Location · type"}
                </div>
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: "1px solid rgba(27,35,64,0.08)",
                    display: "flex", alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {previewSlug ? (
                    <a
                      href={`/spaces/${previewSlug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: 11,
                        color: "var(--gold, #B8913A)",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                      data-testid={`${testid}-hero-preview-view-live`}
                    >
                      View live ↗
                    </a>
                  ) : (
                    <span
                      style={{
                        fontSize: 11,
                        color: "rgba(27,35,64,0.55)",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      View details →
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--gold, #B8913A)",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    Métier
                  </span>
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: 8,
                fontSize: 11,
                color: "rgba(27,35,64,0.55)",
                lineHeight: 1.5,
              }}
            >
              Hero image is <strong>photo #1</strong>. Drag or click <em>Make hero</em> to swap.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
